import { useMemo, useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductModal({ item, open, onOpenChange }) {
  const { addItem } = useCart();
  const [selections, setSelections] = useState({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setSelections({});
      setQty(1);
      setNotes("");
    }
  }, [open]);

  const unitPrice = useMemo(() => {
    let p = item.base_price;
    (item.option_groups || []).forEach((g) => {
      const label = selections[g.name];
      const opt = g.options.find((o) => o.label === label);
      if (opt) p += Number(opt.price_delta || 0);
    });
    return p;
  }, [item, selections]);

  const allChosen = (item.option_groups || [])
    .filter((g) => g.required !== false)
    .every((g) => selections[g.name]);

  const handleAdd = () => {
    if (!allChosen) {
      toast.error("Please choose all required options");
      return;
    }
    const selected_options = (item.option_groups || [])
      .filter((g) => selections[g.name])
      .map((g) => {
        const opt = g.options.find((o) => o.label === selections[g.name]);
        return { group: g.name, label: selections[g.name], price_delta: Number(opt?.price_delta || 0) };
      });
    addItem({
      item_id: item.id,
      name: item.name,
      base_price: item.base_price,
      unit_price: unitPrice,
      quantity: qty,
      selected_options,
      special_instructions: notes.trim(),
    });
    toast.success(`${item.name} added to cart`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="product-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{item.name}</DialogTitle>
          <DialogDescription>{item.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {(item.option_groups || []).map((g) => (
            <div key={g.name} data-testid={`product-option-select-${g.name}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-bold text-stone-900">{g.name}</span>
                {g.required !== false && <span className="text-xs font-medium text-orange-600">Required</span>}
              </div>
              <RadioGroup
                value={selections[g.name] || ""}
                onValueChange={(v) => setSelections((s) => ({ ...s, [g.name]: v }))}
                className="grid grid-cols-1 gap-2"
              >
                {g.options.map((o) => (
                  <label
                    key={o.label}
                    className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 transition-colors duration-200 ${
                      selections[g.name] === o.label
                        ? "border-orange-600 bg-orange-50"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={o.label} id={`${g.name}-${o.label}`} />
                      <span className="text-sm font-medium text-stone-800">{o.label}</span>
                    </div>
                    {Number(o.price_delta) > 0 && (
                      <span className="text-sm font-semibold text-stone-500">+{money(o.price_delta)}</span>
                    )}
                  </label>
                ))}
              </RadioGroup>
            </div>
          ))}

          <div>
            <Label className="text-sm font-bold text-stone-900">Special instructions</Label>
            <Textarea
              data-testid="product-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. extra spicy, no onions"
              className="mt-2 resize-none"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 rounded-full border border-stone-200 p-1">
              <button
                data-testid="product-qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-6 text-center font-bold" data-testid="product-qty-value">{qty}</span>
              <button
                data-testid="product-qty-increase"
                onClick={() => setQty((q) => q + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              data-testid="modal-add-to-cart-button"
              onClick={handleAdd}
              className="rounded-full bg-orange-700 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-orange-800"
            >
              Add · {money(unitPrice * qty)}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
